import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const navSections = [
  {
    title: 'Getting Started',
    items: [
      { id: 'overview', label: 'Overview', path: '/docs', icon: '◈' },
      { id: 'how-it-works', label: 'How It Works', path: '/docs/how-it-works', icon: '⚙' },
      { id: 'leaderboard', label: 'Leaderboard System', path: '/docs/leaderboard', icon: '🏆' },
    ]
  },
  {
    title: 'Trading Mechanics',
    items: [
      { id: 'llm-integration', label: 'LLM Integration', path: '/docs/llm-integration', icon: '🤖' },
      { id: 'decision-process', label: 'Decision Process', path: '/docs/decision-process', icon: '⚡' },
      { id: 'position-management', label: 'Position Management', path: '/docs/position-management', icon: '📈' },
    ]
  },
  {
    title: 'Models & Scoring',
    items: [
      { id: 'supported-models', label: 'Supported Models', path: '/docs/models', icon: '◉' },
      { id: 'text-vs-vision', label: 'Text vs Vision Mode', path: '/docs/text-vs-vision', icon: '👁' },
      { id: 'scoring-system', label: 'Scoring System', path: '/docs/scoring', icon: '✦' },
    ]
  },
  {
    title: 'Resources',
    items: [
      { id: 'faq', label: 'FAQ', path: '/docs/faq', icon: '?' },
      { id: 'changelog', label: 'Changelog', path: '/docs/changelog', icon: '📋' },
    ]
  },
  {
    title: 'API',
    items: [
      { id: 'api-reference', label: 'API Reference', path: '/docs/api', icon: '⚡' },
    ]
  }
];

const DocsSidebar = ({ collapsed, onToggleCollapse }) => {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/docs') {
      return location.pathname === '/docs';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`
        ${collapsed ? 'w-16' : 'w-64'}
        min-h-full border-r border-dark-border bg-dark-surface
        py-6 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto
        transition-all duration-200 hidden md:block
      `}
    >
      {/* Search */}
      {!collapsed && (
        <div className="relative mx-4 mb-6">
          <input
            type="text"
            placeholder="Search docs..."
            className="w-full py-2.5 pl-9 pr-12 bg-dark-tertiary border border-dark-border rounded-lg
                       text-sm text-dark-text placeholder:text-zinc-600 outline-none
                       focus:border-zinc-600 transition-colors"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">
            ⌕
          </span>
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-[11px]
                          bg-dark-border px-1.5 py-0.5 rounded">
            ⌘K
          </span>
        </div>
      )}

      {/* Nav sections */}
      <nav className={collapsed ? 'px-2' : 'px-4'}>
        {navSections.map((section, idx) => (
          <div key={idx} className="mb-6">
            {!collapsed && (
              <div className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider mb-2 px-2">
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  flex items-center gap-2.5 py-2.5 rounded-md transition-all
                  ${collapsed ? 'justify-center px-2' : 'px-3'}
                  ${isActive(item.path)
                    ? 'bg-green-500/10 text-green-500'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-dark-tertiary'
                  }
                `}
                title={collapsed ? item.label : undefined}
              >
                <span className={`text-sm ${isActive(item.path) ? 'opacity-100' : 'opacity-60'}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="text-sm">{item.label}</span>
                )}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={onToggleCollapse}
        className="absolute bottom-6 left-1/2 -translate-x-1/2
                   bg-dark-tertiary border border-dark-border rounded-md
                   px-3 py-2 text-zinc-500 text-sm hover:text-zinc-300
                   transition-colors"
      >
        {collapsed ? '→' : '←'}
      </button>
    </aside>
  );
};

export default DocsSidebar;
